/**
 * `<WeaponFxField>` — actively-pruned pool of weapon-FX prefabs.
 *
 * Mirrors the `<DebrisField>` shape: caller passes a list of
 * `WeaponFxSpawn` requests at each render; the field dedupes by id,
 * adds new entries to the pool, advances the pool by frame time, and
 * unmounts expired entries.
 *
 * Plan reference: docs/dev/visual-engine/voxel-cosmetics-plan.md §4
 * (W4) + §12.4 (particle pooling growth seam).
 */

import { useEffect } from "react";
import { useFrame } from "@react-three/fiber";

import { useFxPool, type UseFxPoolOptions } from "./useFxPool";
import {
  BallisticTracer,
  EnergyBeam,
  ExplosionBurst,
  MissileArc,
  MuzzleFlash,
  ShockwaveRing,
} from "./prefabs";
import type { WeaponFxSpawn } from "./types";

export interface WeaponFxFieldProps extends UseFxPoolOptions {
  /** Spawn requests to add this frame. Component dedupes by `id`. */
  requests: ReadonlyArray<WeaponFxSpawn>;
}

export function WeaponFxField({ requests, maxActive, lifetimeMsOverride }: WeaponFxFieldProps) {
  const pool = useFxPool({ maxActive, lifetimeMsOverride });

  useEffect(() => {
    if (requests.length === 0) return;
    pool.addSpawns(requests);
  }, [requests, pool]);

  useFrame(() => {
    pool.advance(performance.now());
  });

  return (
    <>
      {pool.active.map((fx) => {
        switch (fx.kind) {
          case "ballistic_tracer":
            return <BallisticTracer key={fx.id} fx={fx} />;
          case "energy_beam":
            return <EnergyBeam key={fx.id} fx={fx} />;
          case "missile_arc":
            return <MissileArc key={fx.id} fx={fx} />;
          case "explosion_burst":
            return <ExplosionBurst key={fx.id} fx={fx} />;
          case "muzzle_flash":
            return <MuzzleFlash key={fx.id} fx={fx} />;
          case "shockwave_ring":
            return <ShockwaveRing key={fx.id} fx={fx} />;
          default:
            return null;
        }
      })}
    </>
  );
}
